import { useMemo, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";

import { useUser } from "@components/user-context";
import { type ColumnDef } from "@features/sales-v3/hooks/use-table-columns";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { TrackPerformanceTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

interface Props {
  column: ColumnDef;
}

export const ColumnMenu = ({ column }: Props) => {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.TRACK_PERFORMANCE);
  const [isAboutPanelVisible, setAboutPanelVisible] = useState(false);
  const { selectedCycle } = useSelectedCycle();
  const { user } = useUser();
  const salesPageUrl = useMemo(() => {
    const url = new URL(`${window.location.origin}`);
    url.searchParams.set("attributionModel", "last_touch");
    url.searchParams.set("attributionWindow", "1");
    url.searchParams.set("nbAccountingMode", "accrual");
    url.searchParams.set("timeGranularity", "daily");
    url.searchParams.set("breakdowns", "[]");
    url.searchParams.set("activeTab", "platform");
    // Hashed table with Attributed Rve, Transactions, ROAS, and CAC columns
    url.searchParams.set(
      "tableState",
      // eslint-disable-next-line max-len
      "N4Igxg9gNgrgtgOwMIQQMwJYHMDOIBcA2qBgCYEgBiEATgKZgCGOALnaQAQBKA8gIIBlAPpCAzAAYQAGhAB3AgEYA7KJlYCaRlBx0AvlJLl8VWg2ZtOvQSIBskmfPzLxajVp37DFavSat23PzCQgCc9nKKSgAsrvia2noGIGQUfCwsNBgARjAW3HQAbtIRTkoKsfEeSSnGACo0jAg4jGAsGKh4DooArKog6nHuiV7GVgLFjgpRLv1uCZ7JRiBIfEgTitMVQ7oAujKYAB7sKLCIAOpkLAAWePigAHKMcHQEUd0zAiyMLDC3UyG6XRAA",
    );
    url.searchParams.set(
      "dateRange",
      JSON.stringify({
        startDate: selectedCycle.date_range.start_date,
        endDate: selectedCycle.date_range.end_date,
        type: "fixed",
      }),
    );

    // use a default saved view
    url.searchParams.set("savedViewId", "default");

    url.pathname = `${user.displayUserId}/sales`;

    return url.toString();
  }, [selectedCycle.date_range, user.displayUserId]);

  if (!column.name.startsWith("mta")) {
    return null;
  }

  const metricName = column.title.replace("MTA: ", "");

  return (
    <Popover>
      {({ open }) => (
        <>
          <PopoverButton
            className={clsx(
              "flex justify-center items-center cursor-pointer",
              "rounded w-6 h-6 text-[0.65rem] ml-1",
              {
                "text-interstellar+2 hover:bg-v2-secondary": !open,
                "text-white bg-primary": open,
              },
            )}
          >
            <motion.i
              initial={false}
              animate={{ rotate: open ? 0 : 180 }}
              transition={{ duration: 0.2 }}
              className="fa-regular fa-sharp fa-chevron-up"
            />
          </PopoverButton>
          <PopoverPanel
            className={clsx(
              "absolute py-1 w-60 right-0 z-[1000] bg-v2-primary font-normal",
              "rounded-lg elevation-1 text-sm whitespace-nowrap",
            )}
          >
            {({ close }) =>
              isAboutPanelVisible ? (
                <div
                  className="py-2 px-4"
                  onClick={() => {
                    trackMMMEvent({
                      component:
                        TrackPerformanceTrackedComponent.PERFORMANCE_TRACKING_TABLE,
                      action: "onClick",
                      actionInfo: "Hide 'About this metric' info",
                    });

                    setAboutPanelVisible(false);
                    close();
                  }}
                >
                  <div
                    onClick={() => {
                      setAboutPanelVisible(false);
                      close();
                    }}
                  >
                    This value comes{" "}
                    <a href={salesPageUrl} target="_blank" rel="noreferrer">
                      from here.
                    </a>
                  </div>
                  <div className="flex justify-between">
                    Metric: <b>{metricName}</b>
                  </div>
                  <div className="flex justify-between">
                    Accounting Mode: <b>Accrual</b>
                  </div>
                  <div className="flex justify-between">
                    Attribution Model: <b>Last Touch</b>
                  </div>
                  <div className="flex justify-between">
                    Attribution Window: <b>1 day</b>
                  </div>
                </div>
              ) : (
                <div
                  className={clsx(
                    "w-full text-left py-1.5 px-4 hover:bg-cosmic-4 truncate cursor-pointer",
                  )}
                  onClick={() => {
                    trackMMMEvent({
                      component:
                        TrackPerformanceTrackedComponent.PERFORMANCE_TRACKING_TABLE,
                      action: "onClick",
                      actionInfo: "Show 'About this metric' info",
                    });

                    setAboutPanelVisible(true);
                  }}
                >
                  <i
                    className={clsx(
                      "fa-regular fa-sharp fa-circle-info text-[0.8rem]",
                      "text-primary mt-0.5 mr-1 w-4",
                    )}
                  />
                  About this metric
                </div>
              )
            }
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};
